import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaChartBar, FaListAlt, FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";

import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000GridStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableHandlers } from "../../../u2000Core/types/U2000TableHandlers";
import { NS, U3125ROLES } from "../../U3125";
import { u3125CanDeleteInvoice, u3125CanModifyInvoice, type U3125InvoiceSearchRow } from "../../U3125Service";

interface U3125FactureListToolBarProps<T extends Record<string, unknown>> {
  gridStateHandlers: U2000GridStateHandlers<T>;
  handlerActions?: U2000TableHandlers<T>;
}

export const U3125FactureListToolBar: React.FC<U3125FactureListToolBarProps<Record<string, unknown>>> = ({ gridStateHandlers, handlerActions }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useU2000AuthContext();

  const selectedRows = gridStateHandlers.getSelectedRows() as unknown as U3125InvoiceSearchRow[];
  const disableUpdateBtn = selectedRows.length !== 1 || !u3125CanModifyInvoice(selectedRows[0]?.status);
  const disableDeleteBtn = selectedRows.length === 0 || selectedRows.some((r) => !u3125CanDeleteInvoice(r.status));

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3125ROLES.ADD),
      handleClick: handlerActions?.onAdd,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "forfaitWizard",
      label: t("btnForfaitWizard", { ns: NS }),
      hasRole: hasRole(U3125ROLES.ADD),
      handleClick: () => navigate("/U3125/forfait-wizard"),
      icon: FaListAlt,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3125ROLES.EDIT),
      handleClick: handlerActions?.onModify,
      disabled: disableUpdateBtn,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3125ROLES.DELETE),
      handleClick: handlerActions?.onDelete,
      disabled: disableDeleteBtn,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
    {
      key: "arAgingReport",
      label: t("btnArAgingReport", { ns: NS }),
      hasRole: hasRole(U3125ROLES.READ),
      handleClick: () => navigate("/U3125/ar-aging-report"),
      icon: FaChartBar,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
